import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  Checkbox,
  LoadingSkeleton,
  Table,
  TruncatedAccount,
} from "@narmi/design_system";
import { useNotificationContext } from "cerulean/src/NotificationContext";
import Filters from "byzantine/src/filters";
import type { PaginatedDualApproval } from "byzantine/src/dbbl/businessLogic/entities/dualApprovals/types";
import styles from "../DualApproval.module.scss";
import {
  DUAL_APPROVALS_ROUTE,
  DUAL_APPROVALS_TEMPLATES_PENDING_ROUTE,
} from "../routes";
import useDualApprovalDrawerNavigation from "../hooks/useDualApprovalDrawerNavigation";
import useDualApprovalSelection from "../hooks/useDualApprovalSelection";
import useDualApprovalActions from "../hooks/useDualApprovalActions";
import DualApprovalActionBar from "../common/DualApprovalActionBar";
import DualApprovalCheckboxCell from "../common/DualApprovalCheckboxCell";
import TemplateDualApprovalDrawer from "./TemplateDualApprovalDrawer";

type TemplatePendingTableProps = {
  dualApprovals: PaginatedDualApproval[];
  loading: boolean;
  handleRefresh: () => Promise<API.FetchDualApproval.Response | undefined>;
};

const TemplatePendingTable = ({
  dualApprovals,
  loading,
  handleRefresh,
}: TemplatePendingTableProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const navigate = useNavigate();

  const pendingPath = `/${DUAL_APPROVALS_ROUTE}/${DUAL_APPROVALS_TEMPLATES_PENDING_ROUTE}`;

  const onFetchError = useCallback(() => {
    sendNotification({
      type: "negative",
      text: l10n.getString("error-generic"),
    });
  }, [l10n, sendNotification]);

  const {
    selectedIndex,
    activeDualApproval,
    showControls,
    isDrawerOpen,
    handleOpenDrawer,
    handleCloseDrawer,
    handleOnNext,
    handleOnPrev,
    getPathWithDrawer,
  } = useDualApprovalDrawerNavigation({
    items: dualApprovals,
    basePath: pendingPath,
    loading,
    onFetchError,
  });

  const {
    selectedIds,
    selectedApprovals,
    totalSelected,
    allSelectableAreSelected,
    handleToggleCheckbox,
    handleToggleCheckboxHeader,
    deselectItem,
    setSelectedOnly,
  } = useDualApprovalSelection(dualApprovals);

  const {
    isActionLoading,
    toggledDialogName,
    toggleDialog,
    handleAction,
    handleDrawerAction,
  } = useDualApprovalActions({
    selectedApprovals,
    totalSelected,
    handleRefresh,
    notificationKeys: {
      approve: "template-notif-approved",
      reject: "template-notif-rejected",
    },
    onSelectionReset: (failedIds) => setSelectedOnly(failedIds),
    onDeselectItem: (id) => deselectItem(id),
  });

  const onDrawerAction = (action: "approve" | "reject", note?: string) =>
    handleDrawerAction({
      action,
      note,
      activeDualApproval,
      items: dualApprovals,
      selectedIndex,
      getPathWithDrawer,
      navigate,
      handleCloseDrawer,
    });

  const getOriginatingAccount = (item: PaginatedDualApproval) => {
    const name = item.templateVersion?.institutionAccountName;
    const lastFour = item.templateVersion?.institutionAccountLastFour;
    const { fromAccountDisplay } = item;
    if (name || lastFour) {
      return <TruncatedAccount name={name ?? ""} lastFour={lastFour ?? ""} />;
    }
    if (fromAccountDisplay) return fromAccountDisplay;
    return null;
  };

  const getAmount = (item: PaginatedDualApproval) => {
    if (!item.templateVersion) return "-";
    const { netTotalDollars } = item.templateVersion;
    if (!netTotalDollars || netTotalDollars === "$0") return "-";
    return netTotalDollars;
  };

  const renderRow = (item: PaginatedDualApproval) => (
    <Table.Row
      key={`table-row__${item.id}`}
      onRowClick={() => handleOpenDrawer(item.id)}
    >
      <Table.Cell>
        <DualApprovalCheckboxCell
          isOwnRequest={Boolean(item.isOwnRequest)}
          isChecked={Boolean(selectedIds[item.id])}
          onChange={() => handleToggleCheckbox(item.id)}
        />
      </Table.Cell>
      <Table.Cell>{item.recipientName}</Table.Cell>
      <Table.Cell>{getOriginatingAccount(item)}</Table.Cell>
      <Table.Cell>{item.requesterName}</Table.Cell>
      <Table.Cell>{Filters.longMonthDay(item.createdAt)}</Table.Cell>
      <Table.Cell>{getAmount(item)}</Table.Cell>
      <Table.Cell>
        <div className={styles.viewCtaContainer}>
          <span className={styles.viewCtaText}>
            {l10n.getString("button-view", {}, "View")}
          </span>
          <span className="fontSize--m narmi-icon-chevron-right" />
        </div>
      </Table.Cell>
    </Table.Row>
  );

  if (loading && !isDrawerOpen) {
    return <LoadingSkeleton isLoading />;
  }

  if (dualApprovals.length === 0 && !loading) {
    return (
      <div className={styles.emptyState}>
        {l10n.getString(
          "heading-no-pending-template-approvals",
          {},
          "No pending template approvals"
        )}
      </div>
    );
  }

  return (
    <>
      <div className={styles.tableScrollWrapper}>
        <Table
          colVisibility={[
            "*", // checkbox
            "*", // template name
            "*", // originating account
            "m", // submitted by
            "m", // submitted date
            "*", // amount
            "m", // view cta
          ]}
          colLayout={{
            s: "auto 1fr 1.5fr 1fr",
            m: "auto 1.5fr 1.5fr 1fr .75fr .75fr auto",
            l: "auto 1.5fr 1.5fr 1fr .75fr .75fr auto",
          }}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                <Checkbox
                  checked={allSelectableAreSelected}
                  onChange={() =>
                    handleToggleCheckboxHeader(!allSelectableAreSelected)
                  }
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                {l10n.getString("th-template-name", {}, "Template Name")}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {l10n.getString(
                  "label-originating-account",
                  {},
                  "Originating Account"
                )}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {l10n.getString("th-submitted-by", {}, "Submitted By")}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {l10n.getString("th-submitted-date", {}, "Submitted Date")}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {l10n.getString("th-amount", {}, "Amount")}
              </Table.HeaderCell>
              <Table.HeaderCell>{""}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>{dualApprovals.map(renderRow)}</Table.Body>
        </Table>
      </div>
      <TemplateDualApprovalDrawer
        isOpen={isDrawerOpen}
        showControls={showControls}
        handleClose={handleCloseDrawer}
        handleOnNext={handleOnNext}
        handleOnPrev={handleOnPrev}
        handleApprove={() => onDrawerAction("approve")}
        handleReject={(note) => onDrawerAction("reject", note)}
        isActionLoading={isActionLoading}
        dualApproval={isDrawerOpen ? activeDualApproval : null}
      />
      <DualApprovalActionBar
        totalSelected={totalSelected}
        isActionLoading={isActionLoading}
        toggledDialogName={toggledDialogName}
        toggleDialog={toggleDialog}
        handleAction={handleAction}
        handleDeselectAll={() => handleToggleCheckboxHeader(false)}
        rejectTestId="snackbar-reject-template-approval"
        approveTestId="snackbar-approve-template-approval"
        variant="template"
        hasNoActiveVersion={
          totalSelected > 0 &&
          selectedApprovals.every(
            (approval) =>
              approval.templateVersion?.templateGroupHasActiveVersion === false
          )
        }
      >
        <span className="fontColor--primary fontWeight--semibold">
          {`${totalSelected} ${l10n.getString(
            "snackbar-template-selected",
            {
              totalSelected,
            },
            `template${totalSelected > 1 ? "s" : ""} selected`
          )}`}
        </span>
      </DualApprovalActionBar>
    </>
  );
};

export default TemplatePendingTable;
